import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack, useToast } from '@chakra-ui/react';
import ZaraButton from 'components/custom-components/buttons/Button';
import { useChangeInitialMessageMutation } from 'graphql/generated';
import { useError } from 'error-provider/ErrorProvider';
import { useApolloClient } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';

interface FullAgentConfigGreetingProps {
    greetingData?: string | null;
}

const schema = z.object({
    greetings: z
        .string()
        .min(1, 'Required')
        .refine((val) => val.trim().split(/\s+/).filter(Boolean).length <= 50, 'Maximum 50 words'),
});

type Schema = z.infer<typeof schema>;

const FullAgentConfigGreeting = ({ greetingData }: FullAgentConfigGreetingProps) => {
    const { control, handleSubmit, setValue } = useForm<Schema>({
        defaultValues: {
            greetings: greetingData || '',
        },
        mode: 'onSubmit',
        resolver: zodResolver(schema),
    });

    const { showError } = useError();
    const apolloClient = useApolloClient();
    const toast = useToast();
    const params = useParams();

    const [changeGreetings, { loading: isChangeGreetingsLoading }] = useChangeInitialMessageMutation({
        onError(error) {
            showError(undefined, error);
        },
    });

    const onSubmit = (form: Schema) => {
        changeGreetings({
            variables: {
                initialMessage: form.greetings,
                agentId: params.id as string,
            },
            onCompleted(data) {
                if (data.changeInitialMessage.__typename === 'SuccessType') {
                    toast({
                        title: data.changeInitialMessage.message,
                        status: 'success',
                        isClosable: true,
                    });

                    apolloClient.cache.modify({
                        id: `AgentType:${params.id}`,
                        fields: {
                            initialMessage() {
                                return form.greetings;
                            },
                        },
                    });
                }

                if (data.changeInitialMessage.__typename === 'ErrorType') {
                    toast({
                        title: data.changeInitialMessage.message,
                        status: 'error',
                        isClosable: true,
                    });
                }
            },
        });
    };

    useEffect(() => {
        if (greetingData) {
            setValue('greetings', greetingData);
        }
    }, [greetingData, setValue]);

    return (
        <Stack spacing='4'>
            <div>
                <h3 className='text-lg font-semibold mb-2'>Agent Greeting</h3>
                <p className='text-sm text-[#71717a] mb-4'>
                    Set the initial greeting for your AI agent
                </p>
            </div>

            <Stack as='form' onSubmit={handleSubmit(onSubmit)} alignItems='self-start'>
                <div className='text-xs font-medium text-[#71717a] uppercase tracking-wide mb-2'>
                    Opening Line
                </div>
                <Controller
                    control={control}
                    name='greetings'
                    render={({ field: { onChange, onBlur, value } }) => (
                        <input
                            required
                            placeholder='Enter greeting here (max 50 words)'
                            className='flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                        />
                    )}
                />
                <p className='text-xs text-[#71717a]'>Keep it warm and professional. Maximum 50 words.</p>

                <ZaraButton isLoading={isChangeGreetingsLoading} type='submit' variant='primary' w='auto' fontSize='sm'>
                    Update
                </ZaraButton>
            </Stack>
        </Stack>
    );
};

export default FullAgentConfigGreeting;
